import React, { useState, useEffect, useContext, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import AdminLayout from "../../../../layouts/admin/AdminLayout";
import "../../../../assets/css/admin/dashboardcss/AdminSupport.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { checkLoggedInAdmin } from "../../../../helper/CustomHelper";
import AppContext from "../../../../context/AppContext";

export default function AdminSupport() {
  const { baseUrl, baseUrlForFile } = useContext(AppContext);
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);
  const [reply, setReply] = useState("");
  const chatEndRef = useRef(null);

  const viewAllConversations = () => {
    axios
        .get(`${baseUrl}/admin/support/viewAllConversations`)
      .then((res) => setConversations(res.data.data))
      .catch((err) => console.error(err));
  };

  const fetchMessages = (conid) => {
    const formData = new FormData();
    formData.append("esconversation_id", conid);
    axios
      .post(`${baseUrl}/admin/support/viewAllMessages`, formData)
      .then((res) => {
        setMessages(res.data.data || []);
      })
      .catch((err) => console.error(err));
  };

  useEffect(() => {
    checkLoggedInAdmin(navigate);
    viewAllConversations();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleReply = () => {
    if (!reply.trim() || !selectedConversation) return;

    // Last customer message dhundo
    const lastCustomerMsg = [...messages]
      .reverse()
      .find((m) => m.sender !== "admin");

    if (!lastCustomerMsg) return;

    axios
      .put(
        `${baseUrl}/admin/support/${lastCustomerMsg.id}/reply`,
        { reply: reply }
      )
      .then(() => {
        setReply("");
        fetchMessages(selectedConversation);
        viewAllConversations();
      })
      .catch((err) => console.error(err));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleReply();
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout>
      <div className="csl-support-wrapper">
        <div className="csl-support-header">
          <h1 className="csl-support-title">Support</h1>
          <p className="csl-support-subtitle">Client messages and replies.</p>
        </div>

        <Row className="g-0 csl-chat-container">

          {/* ── LEFT SIDEBAR ── */}
          <Col xs={12} md={4} className="csl-chat-sidebar">
            <div className="csl-sidebar-header">
              <span>Conversations</span>
            </div>
            <div className="csl-sidebar-list">
              {conversations.map((conversation, index) => (
                <div
                  key={conversation.id}
                  className={`csl-sidebar-item ${index === selected ? "csl-sidebar-item--active" : ""}`}
                  onClick={() => {
                    setSelectedConversation(conversation.id);
                    setActiveConversation(conversation);
                    fetchMessages(conversation.id);
                    setSelected(index);
                  }}
                >
                  <div className="csl-sidebar-avatar">
                    {(conversation.escustomer?.fullname || "C")[0].toUpperCase()}
                  </div>
                  <div className="csl-sidebar-info">
                    <div className="csl-sidebar-name-row">
                      <span className="csl-sidebar-name">
                        {conversation.escustomer?.fullname || "Customer"}
                      </span>
                      <span className="csl-sidebar-time">
                        {formatTime(conversation.created_at)}
                      </span>
                    </div>
                    <p className="csl-sidebar-preview">
                      {conversation.essupports?.length > 0
                        ? conversation.essupports[conversation.essupports.length - 1].message
                        : ""}
                    </p>
                  </div>
                  {conversation.status === "new" && (
                    <span className="csl-sidebar-dot" />
                  )}
                </div>
              ))}
            </div>
          </Col>

          {/* ── RIGHT CHAT WINDOW ── */}
          {selected !== null && activeConversation ? (
            <Col xs={12} md={8} className="csl-chat-window">

              {/* Chat Header */}
              <div className="csl-chat-header">
                <div className="csl-chat-header-avatar">
                  {(activeConversation.escustomer?.fullname || "C")[0].toUpperCase()}
                </div>
                <div>
                  <p className="csl-chat-header-name">
                    {activeConversation.escustomer?.fullname || "Customer"}
                  </p>
                  <p className="csl-chat-header-email">
                    {activeConversation.escustomer?.email}
                  </p>
                </div>
              </div>

              {/* Messages */}
              <div className="csl-chat-messages">
                {messages.map((msg) => {
                  const isAdmin = msg.sender === "admin";
                  return (
                    <div
                      key={msg.id}
                      className={`csl-msg-row ${isAdmin ? "csl-msg-row--right" : "csl-msg-row--left"}`}
                    >
                      {/* Customer avatar — left */}
                      {!isAdmin && (
                        <div className="csl-msg-avatar">
                          {(activeConversation.escustomer?.fullname || "C")[0].toUpperCase()}
                        </div>
                      )}

                      <div>
                        <div className={`csl-bubble ${isAdmin ? "csl-bubble--right" : "csl-bubble--left"}`}>
                          {msg.message}
                        </div>
                        <span className={`csl-msg-time ${isAdmin ? "csl-msg-time--right" : ""}`}>
                          {formatTime(msg.created_at)}
                        </span>
                      </div>

                      {/* Admin avatar — right */}
                      {isAdmin && (
                        <div className="csl-msg-avatar csl-msg-avatar--admin">A</div>
                      )}
                    </div>
                  );
                })}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="csl-chat-input-row">
                <textarea
                  className="csl-chat-input"
                  placeholder="Type your reply..."
                  rows={1}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button className="csl-chat-send-btn" onClick={handleReply}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M22 2L11 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

            </Col>
          ) : (
            <Col xs={12} md={8} className="csl-chat-empty">
              <p>Select a conversation to view</p>
            </Col>
          )}

        </Row>
      </div>
    </AdminLayout>
  );
}